/**
 * Ciclo do orçamento (ADR 0120): a janela recorrente em que o teto vale e no começo da qual
 * o realizado zera. Módulo PURO — só datas 'yyyy-MM-dd' e meses 'yyyy-MM', sem banco.
 *
 * Cadências:
 * - `monthly`: ciclos de um mês ancorados no **Dia de início** (1–31). Dia 1 é o
 *   mês-calendário. Em mês mais curto que o dia, começa no último dia do mês (a régua do
 *   fechamento do cartão) e o ciclo seguinte começa no dia certo de novo.
 * - `weekly` / `biweekly`: ciclos de 7 / 14 dias contados a partir da **Data de referência**
 *   — uma data que começa UM ciclo qualquer; os outros se contam dela para frente e para
 *   trás. No semanal só o dia da semana importa; no quinzenal a data também fixa qual das
 *   duas quinzenas é a de referência.
 *
 * Ciclos consecutivos se encostam: cada um termina na véspera do próximo, sem sobreposição
 * nem buraco (o property test em lib/__tests__/budgetCycle.test.ts é o portão).
 */

import {
    addDays,
    addMonths,
    differenceInCalendarDays,
    format,
    getDaysInMonth,
    parseISO,
} from 'date-fns';

export type BudgetCadence = 'monthly' | 'biweekly' | 'weekly';

/**
 * Data de referência que a migração 0040 dá aos semanais/quinzenais que já existiam (uma
 * segunda-feira fixa) e que o repositório usa quando a âncora vem nula (backup de versão
 * antiga). CONSTANTE de propósito: o sync reconcilia por campo (ADR 0030) e dois aparelhos
 * migrando em dias diferentes divergiriam na grade da quinzena se a âncora fosse "hoje".
 */
export const DEFAULT_CYCLE_ANCHOR_DATE = '2026-01-05';

/** Dia de início a partir do qual o ciclo mensal pertence ao mês em que TERMINA. */
export const CYCLE_END_MONTH_THRESHOLD_DAY = 16;

export interface BudgetCycleSpec {
    period: string;
    cycleStartDay: number;
    cycleAnchorDate: string | null;
}

/** Janela inclusiva [startDate, endDate], ambas 'yyyy-MM-dd'. */
export interface BudgetCycle {
    startDate: string;
    endDate: string;
}

const ISO_DATE = 'yyyy-MM-dd';
const ISO_MONTH = 'yyyy-MM';

function toDate(iso: string): Date {
    return parseISO(iso);
}

function toIso(date: Date): string {
    return format(date, ISO_DATE);
}

export function cadenceOf(period: string): BudgetCadence {
    return period === 'weekly' || period === 'biweekly' ? period : 'monthly';
}

/** Tamanho fixo do ciclo em dias, ou null no mensal (que varia com o mês). */
export function cadenceLengthDays(period: string): 7 | 14 | null {
    const cadence = cadenceOf(period);
    if (cadence === 'weekly') return 7;
    if (cadence === 'biweekly') return 14;
    return null;
}

/**
 * Normaliza a âncora pro que a cadência usa: mensal guarda só o Dia de início (âncora
 * nula); semanal/quinzenal guardam só a Data de referência (dia de início preso em 1).
 */
export function normalizeCycleSpec(input: {
    period?: string | null;
    cycleStartDay?: number | null;
    cycleAnchorDate?: string | null;
}): { period: BudgetCadence; cycleStartDay: number; cycleAnchorDate: string | null } {
    const period = cadenceOf(input.period ?? 'monthly');
    if (period === 'monthly') {
        const raw = Math.trunc(Number(input.cycleStartDay ?? 1));
        const cycleStartDay = Number.isFinite(raw) ? Math.min(Math.max(raw, 1), 31) : 1;
        return { period, cycleStartDay, cycleAnchorDate: null };
    }
    const anchor = input.cycleAnchorDate && /^\d{4}-\d{2}-\d{2}$/.test(input.cycleAnchorDate)
        ? input.cycleAnchorDate
        : DEFAULT_CYCLE_ANCHOR_DATE;
    return { period, cycleStartDay: 1, cycleAnchorDate: anchor };
}

/** Ciclo mensal que começa no mês (yyyy-MM) dado. */
function monthlyCycleStartingIn(startDay: number, month: string): BudgetCycle {
    const first = toDate(`${month}-01`);
    const start = Math.min(startDay, getDaysInMonth(first));
    const nextFirst = addMonths(first, 1);
    const nextStart = Math.min(startDay, getDaysInMonth(nextFirst));
    const startDate = new Date(first.getFullYear(), first.getMonth(), start);
    const endDate = addDays(new Date(nextFirst.getFullYear(), nextFirst.getMonth(), nextStart), -1);
    return { startDate: toIso(startDate), endDate: toIso(endDate) };
}

/** O ciclo que contém a data. */
export function cycleContaining(spec: BudgetCycleSpec, date: string): BudgetCycle {
    const { period, cycleStartDay, cycleAnchorDate } = normalizeCycleSpec(spec);
    const length = cadenceLengthDays(period);
    if (length === null) {
        const month = date.slice(0, 7);
        const inMonth = monthlyCycleStartingIn(cycleStartDay, month);
        if (date >= inMonth.startDate) return inMonth;
        const prevMonth = format(addMonths(toDate(`${month}-01`), -1), ISO_MONTH);
        return monthlyCycleStartingIn(cycleStartDay, prevMonth);
    }
    const anchor = toDate(cycleAnchorDate!);
    const diff = differenceInCalendarDays(toDate(date), anchor);
    const index = Math.floor(diff / length);
    const start = addDays(anchor, index * length);
    return { startDate: toIso(start), endDate: toIso(addDays(start, length - 1)) };
}

export function nextCycle(spec: BudgetCycleSpec, cycle: BudgetCycle): BudgetCycle {
    return cycleContaining(spec, toIso(addDays(toDate(cycle.endDate), 1)));
}

export function previousCycle(spec: BudgetCycleSpec, cycle: BudgetCycle): BudgetCycle {
    return cycleContaining(spec, toIso(addDays(toDate(cycle.startDate), -1)));
}

/**
 * O mês (yyyy-MM) a que um ciclo pertence — a regra da MAIORIA DOS DIAS (ADR 0120).
 * Mensal: Dia de início até 15 → o mês em que começa; 16 em diante → o mês em que termina.
 * Semanal/quinzenal: o mês do dia central do ciclo (empate no quinzenal cai pro mês em que
 * termina, coerente com o "16 em diante" do mensal).
 */
export function cycleMonth(spec: BudgetCycleSpec, cycle: BudgetCycle): string {
    const { period, cycleStartDay } = normalizeCycleSpec(spec);
    const length = cadenceLengthDays(period);
    if (length === null) {
        return cycleStartDay < CYCLE_END_MONTH_THRESHOLD_DAY
            ? cycle.startDate.slice(0, 7)
            : cycle.endDate.slice(0, 7);
    }
    const middle = addDays(toDate(cycle.startDate), Math.floor(length / 2));
    return format(middle, ISO_MONTH);
}

/** Os ciclos que pertencem ao mês, em ordem. Mensal: exatamente um. Semanal: 4 ou 5. */
export function cyclesOfMonth(spec: BudgetCycleSpec, month: string): BudgetCycle[] {
    const { period, cycleStartDay } = normalizeCycleSpec(spec);
    const length = cadenceLengthDays(period);
    if (length === null) {
        if (cycleStartDay < CYCLE_END_MONTH_THRESHOLD_DAY) {
            return [monthlyCycleStartingIn(cycleStartDay, month)];
        }
        const prevMonth = format(addMonths(toDate(`${month}-01`), -1), ISO_MONTH);
        return [monthlyCycleStartingIn(cycleStartDay, prevMonth)];
    }
    const first = toDate(`${month}-01`);
    const monthEnd = toIso(addDays(addMonths(first, 1), -1));
    // Começa um ciclo antes do mês pra pegar o que atravessa a virada.
    let cycle = cycleContaining(spec, toIso(addDays(first, -length)));
    const result: BudgetCycle[] = [];
    while (cycle.startDate <= monthEnd) {
        if (cycleMonth(spec, cycle) === month) result.push(cycle);
        cycle = nextCycle(spec, cycle);
    }
    return result;
}

/**
 * O Ciclo do mês (glossário): o que as telas mensais mostram para um mês. Mensal: o único
 * ciclo do mês. Semanal/quinzenal: o em curso se o mês é o atual, senão o último do mês.
 */
export function cycleForMonth(spec: BudgetCycleSpec, month: string, today: string): BudgetCycle {
    const cycles = cyclesOfMonth(spec, month);
    if (cadenceLengthDays(spec.period) === null) return cycles[0];
    if (today.slice(0, 7) === month) return cycleContaining(spec, today);
    return cycles[cycles.length - 1];
}

/** Verdadeiro quando o ciclo é exatamente um mês-calendário (o padrão, dia 1). */
export function isCalendarMonthCycle(cycle: BudgetCycle): boolean {
    if (!cycle.startDate.endsWith('-01')) return false;
    const first = toDate(cycle.startDate);
    return cycle.endDate === toIso(addDays(addMonths(first, 1), -1));
}

/** Menor janela que cobre todos os ciclos dados (pra UMA query servir a todos). */
export function cyclesEnvelope(cycles: BudgetCycle[]): BudgetCycle | null {
    if (cycles.length === 0) return null;
    let startDate = cycles[0].startDate;
    let endDate = cycles[0].endDate;
    for (const cycle of cycles) {
        if (cycle.startDate < startDate) startDate = cycle.startDate;
        if (cycle.endDate > endDate) endDate = cycle.endDate;
    }
    return { startDate, endDate };
}

export function dateInCycle(cycle: BudgetCycle, date: string): boolean {
    return date >= cycle.startDate && date <= cycle.endDate;
}
